'use strict';

var FirebaseIndirectArray = require('./FirebaseIndirectArray');
var KayleeBase = require('./KayleeBase');
var KayleeCache = require('./KayleeCache');
var KayleeConnectionState = require('./KayleeConnectionState');
var KayleeRoom = require('./KayleeRoom');
var KayleeUser = require('./KayleeUser');
var KayleeUtil = require('./KayleeUtil');
var log = require('../log/Log').channel('KayleeRooms');

var KayleeRooms = {
    fromJson: fromJson,
    getRooms: getRooms,
};

function fromJson(data) {
    var reqProps = [ '$siteCacheKey', '$userId' ];
    return FirebaseIndirectArray.$fromJson(data, 'Rooms', reqProps, reqProps,
        function(data) {
            var site = KayleeCache.get(data.$siteCacheKey);
            return getRooms(site, data.$userId);
        }
    );
}

function getRooms(site, userId) {    
    if (typeof userId === 'undefined' || userId === null) {
        if (KayleeBase.app.auth().currentUser === null) return null;
        userId = KayleeBase.app.auth().currentUser.uid;
    }

    var key = 'Rooms: ' + site.$id + ' ' + userId;
    var rooms = KayleeCache.get(key);

    if (rooms === null) {
        rooms = new Rooms(site, userId);
        KayleeCache.put(key, rooms);
    }

    return rooms;
}

function Rooms(site, userId) {
    var self = this;

    FirebaseIndirectArray.call(self, null, KayleeRoom.getRoom);
    self.$site = site;
    self.$user = KayleeUser.getUser(userId);
    KayleeUtil.bindPrototypeMethods(Rooms.prototype, self, self.$items);
    this.$items.$self = this;

    if (KayleeConnectionState.isWorkingOnline()) {
        self.$site.$loaded()
        .then(function() {
            return self.$user.$loaded();
        }).then(function() {
            var srcRef = self.$getSrcRef(
                self.$user.$canWatchAllRoomsForSite(site.companyId, site.$id));
            self.$setSrcRef(srcRef);

            if (KayleeBase.isDbRoleEnabled) {
                self.$user.$ref.child('roles/role').on('value', self.$dbRoleChangedCb,
                    self.$dbRoleCancelledCb, self);
            }

            self.$user.$ref.child('roles/companies/' + site.companyId + '/role')
            .on('value', self.$companyRoleChangedCb,
                self.$companyRoleCancelledCb, self);
            self.$user.$ref.child('roles/companies/' + site.companyId + '/sites/' +
                site.$id + '/role')
            .on('value', self.$siteRoleChangedCb,
                self.$siteRoleCancelledCb, self);
        });
    }

    return self.$items;
}

Rooms.prototype = Object.create(FirebaseIndirectArray.prototype);
Rooms.prototype.constructor = Rooms;

Rooms.prototype.$toJson = function() {
    return FirebaseIndirectArray.prototype.$toJson.call(this,
        [], { $siteCacheKey: this.$site.$cacheKey, $userId: this.$user.$id });
};

Rooms.prototype.toString = function() {
    return '[Rooms for ' + this.$user + ' in ' + this.$site + ']';
};

Rooms.prototype.$getSrcRef = function(canWatchAllRoomsForSite) {
    return canWatchAllRoomsForSite ?
        KayleeBase.app.database().ref('index/roomsBySite/' + this.$site.$id) :
        this.$user.$ref.child('roles/companies/' + this.$site.companyId +
            '/sites/' + this.$site.$id + '/rooms');
};

Rooms.prototype.$dbRoleChangedCb = function(snap) {
    var newDbRole = snap.val();
    var oldWatchAll = this.$user.$canWatchAllRoomsForSite(this.$site.companyId,
        this.$site.$id);
    var newWatchAll = this.$user.$canWatchAllRoomsForSite(this.$site.companyId,
        this.$site.$id, newDbRole);

    if (this.$user.$getRoleForDb() !== newDbRole) {
        log.debug('db role changed to ' + newDbRole + ' for ' + this.$user +
            ' in ' + this.$company);
    }

    if (oldWatchAll !== newWatchAll) {
        this.$setSrcRef(this.$getSrcRef(newWatchAll));
    }
};

Rooms.prototype.$dbRoleCancelledCb = function(err) {
    //log.debug('db role cancelled for ' + this.$user + ', error: ' + err);

    // FirebaseIndirectArray takes care of cleaning up itself when the
    // subscription for its srcRef is cancelled, then KayleeCache takes care of
    // removing the array from the cache via FirebaseIndirectArray's $onDestroy.
    // All we need to do is stop watching the db role.

    this.$user.$ref.child('roles/role')
    .off('value', this.$dbRoleChangedCb, this);
};

Rooms.prototype.$companyRoleChangedCb = function(snap) {
    var newCompanyRole = snap.val();
    var dbRole = this.$user.$getRoleForDb();
    var oldWatchAll = this.$user.$canWatchAllRoomsForSite(this.$site.companyId,
        this.$site.$id, dbRole);
    var newWatchAll = this.$user.$canWatchAllRoomsForSite(this.$site.companyId,
        this.$site.$id, dbRole, newCompanyRole);

    if (this.$user.$getRoleForCompany(this.$site.companyId) !== newCompanyRole) {
        log.debug('company role changed to ' + newCompanyRole + ' for ' +
            this.$user + ' in ' + this.toString());
    }

    if (oldWatchAll !== newWatchAll) {
        this.$setSrcRef(this.$getSrcRef(newWatchAll));
    }
};

Rooms.prototype.$companyRoleCancelledCb = function(err) {
    //log.debug('company role cancelled for ' + this.$user + ' in ' +
    //    this.toString());

    // FirebaseIndirectArray takes care of cleaning up itself when the
    // subscription for its srcRef is cancelled, then KayleeCache takes care of
    // removing the array from the cache via FirebaseIndirectArray's $onDestroy.
    // All we need to do is stop watching the company role.

    this.$user.$ref.child('roles/companies/' + this.$site.companyId + '/role')
    .off('value', this.$companyRoleChangedCb, this);
};

Rooms.prototype.$siteRoleChangedCb = function(snap) {
    var newSiteRole = snap.val();
    var dbRole = this.$user.$getRoleForDb();
    var companyRole = this.$user.$getRoleForCompany(this.$site.companyId);
    var oldWatchAll = this.$user.$canWatchAllRoomsForSite(this.$site.companyId,
        this.$site.$id, dbRole, companyRole);
    var newWatchAll = this.$user.$canWatchAllRoomsForSite(this.$site.companyId,
        this.$site.$id, dbRole, companyRole, newSiteRole);

    if (this.$user.$getRoleForSite(this.$site) !== newSiteRole) {
        log.debug('site role changed to ' + newSiteRole + ' for ' +
            this.$user + ' in ' + this.toString());
    }

    if (oldWatchAll !== newWatchAll) {
        this.$setSrcRef(this.$getSrcRef(newWatchAll));
    }
};

Rooms.prototype.$siteRoleCancelledCb = function(err) {
    //log.debug('site role cancelled for ' + this.$user + ' in ' + this.$site);

    // FirebaseIndirectArray takes care of cleaning up itself when the
    // subscription for its srcRef is cancelled, then KayleeCache takes care of
    // removing the array from the cache via FirebaseIndirectArray's $onDestroy.
    // All we need to do is stop watching the site role.

    this.$user.$ref.child('roles/companies/' + this.$site.companyId +
        '/sites/' + this.$site.$id + '/role')
    .off('value', this.$siteRoleChangedCb, this);
};

Rooms.prototype.$getAlertCount = function() {
    var alertCount = 0;

    this.$items.forEach(function(room) {
        if (room.$hasAlert()) alertCount++;
    });

    return alertCount;
};

Rooms.prototype.$getControllerCount = function() {
    var controllerCount = 0;

    this.$items.forEach(function(room) {
        controllerCount += room.$getControllerCount();
    });

    return controllerCount;
};

Rooms.prototype.$getInCallCount = function() {
    var inCallCount = 0;

    this.$items.forEach(function(room) {
        if (room.$isInCall()) inCallCount++;
    });

    return inCallCount;
};

module.exports = KayleeRooms;
